import mondaySdk from 'monday-sdk-js';
import "@vibe/core/tokens";
import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import './App.css';
import { ReactComponent as BeelabxLogo } from './assets/beelabx-lockup.svg';
import { setPendingTour } from './tours/pendingTour';
import { Activity, Form, Help, Upgrade } from '@vibe/icons';

const monday = mondaySdk();

/*
 * Two applications share this bundle, and until now they shared one download.
 *
 * `/form/:hash` and `/form_nps/:hash` are respondent-facing: someone outside the
 * account, often on a phone, filling in a form once. Everything else is the
 * owner-facing app inside monday. A respondent was downloading the whole editor
 * — drag-and-drop, the tours, the settings panels — to type their name into a
 * text box, and an owner was downloading the public form renderer.
 *
 * Split at the route, which is the point where the two can never both be needed.
 * The management tabs are split from each other as well: three of the four are
 * rarely opened, and Forms is the only one that has to be fast.
 */
const FormContainer = lazy(() => import('./public-form/FormContainer'));
const NpsFormContainer = lazy(() => import('./public-form/NpsFormContainer'));
const MainView = lazy(() => import('./management/MainView'));
const SubscriptionView = lazy(() => import('./app-views/SubscriptionView'));
const BillingForm = lazy(() => import('./app-views/BillingForm'));
const HelpView = lazy(() => import('./app-views/HelpView'));

/*
 * Deliberately plain, and deliberately not a spinner.
 *
 * This shows for the moment between the shell painting and a route chunk
 * arriving. A spinner here would compete with whatever loading state the route
 * itself shows a moment later; empty space reads as "still arriving" and does
 * not flash.
 */
const RouteFallback = () => <div style={{ minHeight: 240 }} aria-hidden />;

const NAV_ITEMS = [
  { id: 0, label: 'Forms', icon: Form },
  { id: 1, label: 'Subscription', icon: Activity },
  { id: 2, label: 'Plans', icon: Upgrade },
  { id: 3, label: 'Help', icon: Help },
];

class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      context: {},
      currentTab: 0,
    };
    this.changeTab = this.changeTab.bind(this);
    this.renderTabUI = this.renderTabUI.bind(this);
    this.startTour = this.startTour.bind(this);
  }

  // Queue a tour, then switch to the Forms tab. MainView opens a matching form
  // editor once its forms load, and the editor auto-starts the tour on mount.
  startTour(tourKey) {
    setPendingTour(tourKey);
    this.changeTab(0);
  }

  componentDidMount() {
    monday.get('context').then((res) => {
      this.setState({ context: res.data });
    });
  }

  changeTab(tabId) {
    this.setState({ currentTab: tabId });
  }

  renderTabUI() {
    const { currentTab, context } = this.state;
    return (
      <div className='app-shell'>
        {/* ── Top Navigation Bar ── */}
        <header className='app-topbar'>
          <nav className='app-topbar-nav'>
            {NAV_ITEMS.map((item) => {
              const isActive = currentTab === item.id;
              return (
                <button
                  key={item.id}
                  className={`app-topbar-tab ${isActive ? 'app-topbar-tab--active' : ''}`}
                  onClick={() => this.changeTab(item.id)}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>
          <a
            href='https://www.beelabxapps.com/'
            target='_blank'
            rel='noopener noreferrer'
            className='app-topbar-logo'
          >
            <BeelabxLogo role='img' aria-label='BeelabX' />
          </a>
        </header>

        {/* ── Main Content ── */}
        {/* Inside the nav, not around it: the top bar is part of the shell and
            should stay on screen while a tab's chunk loads, rather than the
            whole page blanking on every tab change. */}
        <main className='app-content'>
          <Suspense fallback={<RouteFallback />}>
            {currentTab === 0 && <MainView />}
            {currentTab === 1 && <SubscriptionView accountId={context?.account?.id} />}
            {currentTab === 2 && <BillingForm />}
            {currentTab === 3 && <HelpView onStartTour={this.startTour} />}
          </Suspense>
        </main>
      </div>
    );
  }

  render() {
    return (
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path='/form/:hash' element={<FormContainer />} />
            <Route path='/form_nps/:hash' element={<NpsFormContainer />} />
            <Route path='*' element={this.renderTabUI()} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }
}

export default App;

